import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {IoIosRefresh} from "react-icons/io";
import API from "../../context/API";
import SearchBar from "../../theme/Layout/Searchbar";

function SoldOutCustomizations({tabIndex, role }) {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false); // Separate state for refresh loading
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null); // To store last updated time
  const [searchQuery, setSearchQuery] = useState("");

  const bgColor = useColorModeValue("white", "gray.800");

  const { isOpen, onClose } = useDisclosure();
  const toast = useToast();
  const sortByUpdatedAt = (data) => {
    if (!Array.isArray(data)) {
      throw new Error("Input data must be an array.");
    }
    return [...data].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  };

  const fetchSoldOutItems = useCallback(async () => {
    try {
      setRefreshing(true);
      const response = await API.get(`/customizations?page=1&limit=100`);

      if (response.data.success) {
        const soldOutItems = response.data.data.filter(customization => Boolean(customization.sold_out) === true);
        const itemsData = sortByUpdatedAt(soldOutItems);
        setItems(itemsData);
        setFilteredItems(itemsData);
        setLastUpdated(new Date().toLocaleString());
      } else {
        setError("Failed to fetch sold-out items.");
      }
    } catch (err) {
      console.error("Error fetching sold out customizations:", err);
      setError("An error occurred while fetching sold-out items.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (tabIndex === 6) {
      fetchSoldOutItems();
    }
  }, [tabIndex, fetchSoldOutItems]);

  const handleToggleSoldOut = async (item) => {
    try {
      
      // Use the customization rules endpoint to update sold_out status
      const response = await API.put(`/customizations/${item.id}/rules`, {
        max_quantity: item.max_quantity || 0,
        min_quantity: item.min_quantity || 0,
        multiple_selections: item.multiple_selections || false,
        multiple_selections_quantity: item.multiple_selections_quantity || 0,
        sold_out: false, // Set to false to make it available again
        note: item.note || "",
      });


      if (response.data.success) {
        toast({
          title: "Customization status updated.",
          description: "The customization is now available.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        // Refresh the list after toggling
        await fetchSoldOutItems();
        onClose();
      } else {
        throw new Error("Failed to update customization status");
      }
    } catch (error) {
      console.error("Error updating customization:", error);
      toast({
        title: "An error occurred.",
        description: "Unable to update the customization status.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const confirmToggleSoldOut = (item) => {
    // onOpen();
    handleToggleSoldOut(item)
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    const results = items.filter((item) =>
      item.customization.toLowerCase().includes(query.toLowerCase()) ||
      (item.note && item.note.toLowerCase().includes(query.toLowerCase()))
    );
    setFilteredItems(results);
  };

  const RefreshButton = () => (
    <IconButton
      icon={<IoIosRefresh />}
      size={"sm"}
      onClick={fetchSoldOutItems}
      isLoading={refreshing}
      loadingText="Refreshing..."
      mx={4}
      px={2}
    />
  );

  if (loading && !refreshing) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Text color="red.500">{error}</Text>
      </Box>
    );
  }

  return (
    <Box my={"4"}>
      <Flex
        justify={{ base: "flex-start", md: "space-between" }}
        //align="center"
        marginBottom="4"
        direction={{ base: "column", md: "row" }}
      >
        <Box>
          <Heading size={"xl"} letterSpacing={-2}>
            Sold out customizations
          </Heading>
          <Text fontSize={"sm"}>
            List of all sold out customization, you can mark new ones as sold out, they
            will appear here.
          </Text>
        </Box>
        <Flex align="center">
          <Text fontSize="sm" color="gray.500">
            Last updated: {lastUpdated || "Never"}
          </Text>
          <RefreshButton />
        </Flex>
      </Flex>
      <SearchBar
        items={items.map((item) => item.customization)}
        onSearch={handleSearch}
        placeholder="Search customizations..."
        initialValue={searchQuery}
        variant="filled"
        borderRadius="full"
        width="100%"
      />
      <Box p={4} bg={bgColor} borderRadius="md" overflowX="auto">
        <Table variant="simple" borderRadius="md" mt="4" minWidth="600px">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Description</Th>
              {/* <Th>Price</Th> */}
              <Th textAlign={"center"}>Sold out</Th>
            </Tr>
          </Thead>
          <Tbody>
            {refreshing ? (
              <Tr>
                <Td colSpan="5">
                  <Flex
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <Spinner size="lg" />
                  </Flex>
                </Td>
              </Tr>
            ) : (
              filteredItems.map((item) => (
                <Tr key={item.id}>
                  <Td>{item.customization}</Td>
                  <Td>{item.note || "No description available"}</Td>
                  {/* <Td>
                    $
                    {item.price !== null && item.price !== undefined
                      ? (item.price || 0).toFixed(2)
                      : "Free"}
                  </Td> */}
                  <Td>
                    <Flex justifyContent={"center"}>
                      <Switch
                        isChecked={item.sold_out}
                        onChange={() => confirmToggleSoldOut(item)}
                      />
                    </Flex>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
      {/* Toggle Sold-Out Status Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Status Change</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to mark this item as available again?
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="green" mr={3} onClick={handleToggleSoldOut}>
              Yes, Make Available
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
export default SoldOutCustomizations;
